import CircularProgress from '@mui/material/CircularProgress'
import TextField from '@mui/material/TextField'
import { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuthenticator } from '@aws-amplify/ui-react'
import PrimaryButton from '../../../../../../../global/components/buttons/primary-button/PrimaryButton'
import SecondaryButton from '../../../../../../../global/components/buttons/secondary-button/SecondaryButton'
import PrimaryCheckbox from '../../../../../../../global/components/checkbox/Checkbox'
import { ToastNotificationContext } from '../../../../../../../global/context/toast-context/ToastNotificationContext'
import { fetchData } from '../../../../../../../global/utils/fetch'
import getUsername from '../../../../../../../global/utils/getUsername'
import { WoDetailContext } from '../../../../../context/context'
import { ITechnician } from '../../../../../../../../../app/entities/Technician'
import { IOrderNote } from '../../../../../../../../../app/entities/OrderNote'
import { NoteType } from '../../../../../../../../../app/types/order-notes'
import styles from '../styles/styles.module.css'
import { cloneOrder } from '../clone-order/api'
import { assignTrOrderNumber } from '../assign-tr-number/api'
import { getOrderNotes, createOrderNote } from '../../../notes/api'
import { getTechAssignedToWorkOrder } from '../../../../../api'
import { buildReasonNote, buildOgNotesNote } from './helpers'

const REWORK_TR_NUMBER = 'REWORK'
const TECHNICIAN_NOTE: NoteType = 'technician_note'

interface IProps {
  handleClose: Function
}

const CreateRework = (props: IProps) => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { data: order, queryOrderData } = useContext(WoDetailContext)
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)
  const { user } = useAuthenticator((context) => [context.user])
  const username = getUsername(user)

  const [reason, setReason] = useState<string>('')
  const [isChecked, setIsChecked] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [ogTechName, setOgTechName] = useState<string>('')
  const [isTechLoading, setIsTechLoading] = useState<boolean>(true)

  // Fetch the OG work order's technician so CS can see who did the original job
  useEffect(() => {
    let isMounted = true
    const loadTechnician = async () => {
      if (!id) return
      setIsTechLoading(true)
      try {
        const technician = await fetchData<ITechnician | undefined>(
          getTechAssignedToWorkOrder(id),
        )
        if (isMounted) setOgTechName(technician?.name ?? '')
      } catch (err) {
        // getTechnicianByOrderObjectId can throw when there is no route/day plan
        console.error('Error loading OG technician:', err)
        if (isMounted) setOgTechName('')
      } finally {
        if (isMounted) setIsTechLoading(false)
      }
    }
    loadTechnician()
    return () => {
      isMounted = false
    }
  }, [id])

  const handleCreateRework = async () => {
    if (!id) return
    try {
      setIsLoading(true)

      // 1. Clone the OG order (returns the new order's objectId)
      const newOrderId = await fetchData<string>(cloneOrder(id))
      if (!newOrderId) {
        throw new Error('Newly cloned order ID is undefined or null')
      }

      // 2. Attach the universal REWORK Tire Rack order number
      await fetchData(assignTrOrderNumber(newOrderId, REWORK_TR_NUMBER))

      // 3. Add the REWORK reason note (with OG technician) to the new order
      const reasonNote = buildReasonNote(ogTechName, reason)
      await fetchData(
        createOrderNote(newOrderId, TECHNICIAN_NOTE, reasonNote, username),
      )

      // 4. Carry over the OG work order notes as a single technician note
      const ogNotes = (await fetchData<IOrderNote[]>(getOrderNotes(id))) || []
      const ogNotesNote = buildOgNotesNote(
        ogNotes,
        order?.orderVehicles || [],
        order?.note,
      )
      if (ogNotesNote) {
        await fetchData(
          createOrderNote(newOrderId, TECHNICIAN_NOTE, ogNotesNote, username),
        )
      }

      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message:
          'REWORK created successfully; you have been redirected to the new order.',
        severity: 'success',
      })

      navigate(`/work-orders/${newOrderId}`)
      await queryOrderData(newOrderId)
      props.handleClose()
    } catch (error) {
      console.error(error)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'An error occurred while creating the REWORK.',
        severity: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={styles.inputsContainer}>
      {isLoading && (
        <div className={styles.progressContainer}>
          <CircularProgress />
        </div>
      )}

      <div style={{ marginBottom: '16px' }}>
        <strong>OG Technician:</strong>{' '}
        {isTechLoading ? 'Loading…' : ogTechName || 'Unavailable'}
      </div>

      <div className={styles.topInputsContainer}>
        <TextField
          label='Reason for REWORK'
          required
          multiline
          minRows={3}
          fullWidth
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </div>

      <div className={styles.checkBoxContainer}>
        <PrimaryCheckbox
          onChange={() => setIsChecked(!isChecked)}
          checked={isChecked}
          label={'Yes I want to create a REWORK from this order'}
        />
      </div>

      <div className={styles.buttonContainer}>
        <SecondaryButton
          buttonName='Cancel'
          onClick={() => props.handleClose()}
        />
        <PrimaryButton
          buttonName='Create REWORK'
          disabled={!isChecked || !reason.trim() || isLoading}
          onClick={handleCreateRework}
        />
      </div>
    </div>
  )
}

export default CreateRework
